import type {CreateUserFormPayload} from "./create-user.schema";
import type {MutationCallbacks} from "../../types/callbacks";
import type {User} from "@project/sdk/admin";

import {useMutation, useQueryClient} from "@tanstack/react-query";

import {userKeys} from "../../user-keys";

import {createUserMutationOptions} from "./create-user.mutation-options";

/**
 * Command hook: Create a new user.
 *
 * DEFAULT BEHAVIOR (always applied):
 * - Invalidate list queries on success so the new user appears in the table
 *
 * USER CALLBACKS (optional, applied after defaults):
 * - onSuccess: e.g., show toast, close modal
 * - onError: e.g., show error toast
 *
 * @param callbacks - Optional callbacks for custom logic
 *
 * @example
 * const mutation = useCreateUser({
 *   onSuccess: (user) => showToast(`Created ${user.name}`),
 *   onError: (err) => showToast(`Error: ${err.message}`),
 * });
 */
export function useCreateUser(callbacks?: Partial<MutationCallbacks<CreateUserFormPayload, User>>) {
  const queryClient = useQueryClient();
  const listFilter = {queryKey: userKeys.lists()};

  return useMutation({
    ...createUserMutationOptions(),

    onSuccess: (user) => {
      // ─── DEFAULT: Invalidate lists so the new user appears ────────
      void queryClient.invalidateQueries(listFilter);

      // ─── USER: Custom onSuccess callback ──────────────────────────
      callbacks?.onSuccess?.(user);
    },

    onError: (err, params, ctx) => {
      // ─── USER: Custom onError callback ───────────────────────────
      callbacks?.onError?.(err, params, ctx);
    },
  });
}
